import React, { useEffect, useRef } from 'react';

// Animated deep-space canvas background
// Stars twinkle, nebula glows pulse slowly — no interaction, pure ambiance
export default function StarfieldCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let time = 0;

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    // Stars — varied sizes, speeds, brightness phases
    const stars = Array.from({ length: 220 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.4 + 0.2,
      phase: Math.random() * Math.PI * 2,
      freq: Math.random() * 0.8 + 0.3,
      base: Math.random() * 0.45 + 0.25,
      amp: Math.random() * 0.25 + 0.05,
      // subtle drift
      dx: (Math.random() - 0.5) * 0.000015,
      dy: (Math.random() - 0.5) * 0.000008,
    }));

    // A few larger "foreground" stars
    const brightStars = Array.from({ length: 12 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.8 + 1.2,
      phase: Math.random() * Math.PI * 2,
      freq: Math.random() * 0.4 + 0.15,
      base: 0.6,
      amp: 0.3,
    }));

    // Nebula blobs — soft radial gradients
    const nebulae = [
      { x: 0.08, y: 0.15, rFactor: 0.38, r: 99,  g: 102, b: 241, alpha: 0.055 },
      { x: 0.88, y: 0.72, rFactor: 0.30, r: 56,   g: 189, b: 248, alpha: 0.045 },
      { x: 0.52, y: 0.95, rFactor: 0.28, r: 168,  g: 85,  b: 247, alpha: 0.04  },
      { x: 0.72, y: 0.08, rFactor: 0.22, r: 52,   g: 211, b: 153, alpha: 0.025 },
    ];

    function draw() {
      const w = canvas.width;
      const h = canvas.height;

      // Deep space background
      ctx.fillStyle = '#010614';
      ctx.fillRect(0, 0, w, h);

      // Nebula glows
      nebulae.forEach((n) => {
        const cx = n.x * w;
        const cy = n.y * h;
        const radius = n.rFactor * Math.min(w, h);
        const pulse = 1 + Math.sin(time * 0.25 + n.x * 5) * 0.12;
        const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius * pulse);
        grd.addColorStop(0, `rgba(${n.r},${n.g},${n.b},${n.alpha})`);
        grd.addColorStop(0.5, `rgba(${n.r},${n.g},${n.b},${n.alpha * 0.4})`);
        grd.addColorStop(1, 'transparent');
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, w, h);
      });

      // Background stars
      stars.forEach((s) => {
        s.x += s.dx;
        s.y += s.dy;
        if (s.x < 0) s.x = 1;
        if (s.x > 1) s.x = 0;
        if (s.y < 0) s.y = 1;
        if (s.y > 1) s.y = 0;

        const alpha = s.base + Math.sin(time * s.freq + s.phase) * s.amp;
        ctx.beginPath();
        ctx.arc(s.x * w, s.y * h, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200, 218, 255, ${Math.max(0, alpha)})`;
        ctx.fill();
      });

      // Bright foreground stars with a soft glow
      brightStars.forEach((s) => {
        const alpha = s.base + Math.sin(time * s.freq + s.phase) * s.amp;
        const x = s.x * w;
        const y = s.y * h;

        // Glow halo
        const grd = ctx.createRadialGradient(x, y, 0, x, y, s.r * 3.5);
        grd.addColorStop(0, `rgba(200, 218, 255, ${alpha * 0.5})`);
        grd.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(x, y, s.r * 3.5, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(x, y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(230, 238, 255, ${Math.min(1, alpha * 1.2)})`;
        ctx.fill();
      });

      time += 0.016;
      animId = requestAnimationFrame(draw);
    }

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        display: 'block',
      }}
    />
  );
}
